import { EscrowWireDisbursementCategoriesOptions } from 'components/order/escrow/disbursements/achPushDisbursements/createWireFromACH/EscrowWireDisbursementCategoriesOptions';
import React from 'react';
import { IWireDisbursementFormData } from 'types/order/escrow/wires/IWireDisbursementFormData';
import { TextField, NumberField, SelectField, AreaField } from 'ui-toolkit';
import { useField } from 'formik';
import { SendWireButton } from './SendWireButton';
import { IBaseDisbursementContainerProps } from '../IBaseDisbursementContainerProps';
import { AccountNumber } from '../shared/AccountNumber';
import { HoldbackContainer } from '../holdbacks/HoldbackContainer';
import { VoidWireButton } from '../Voiding/VoidWireButton';
import { DateFormat, getDateString } from '../../../../../../ui-toolkit/utilities/getDateString';
import { WireABAField } from './formFields/WireABAField';
import { GiactField } from './formFields/GiactField';

export const WireDisbursementContainer: React.FC<IBaseDisbursementContainerProps> = ({
  disbursementType,
  isDirty,
  isSubmitting,
  TypeChange,
}) => {
  const [{ value: data }] = useField<IWireDisbursementFormData>('value');

  const isReadOnly = disbursementType.isReadOnly(data) || !disbursementType.hasEditPermission;
  const showSend = !data.isNew && !isReadOnly;
  const canSend = !isDirty && !isSubmitting && !data.holdback;

  return (
    <>
      <div className={'my-4 flex basis-full flex-wrap gap-y-2'}>
        <NumberField
          data-test-id={'amount-field'}
          className={'basis-1/4 pr-2'}
          name={`value.amountCents`}
          isRequired
          label={'Amount'}
          valueIsCents
          mode="currency"
          readOnly={isReadOnly}
        />
        <SelectField
          className={'basis-1/3 pr-2'}
          name={`value.category`}
          options={EscrowWireDisbursementCategoriesOptions}
          label={'Category'}
          isRequired
          isDisabled={isReadOnly}
        />
        {TypeChange}
        <TextField
          className={'basis-full'}
          name={`value.beneficiary`}
          isRequired
          readOnly={isReadOnly}
          label={'Beneficiary Name'}
        />
        <HoldbackContainer id={data.id} isReadOnly={isReadOnly} isDirty={isDirty} />
        <WireABAField isReadOnly={isReadOnly} />
        <AccountNumber baseName={'value'} isDirty={isDirty} isReadOnly={isReadOnly} />
        <AreaField
          className={'basis-full'}
          name={`value.memo`}
          label={'Memo Line'}
          readOnly={isReadOnly}
          maxCharacterCount={160}
        />
        <GiactField isReadOnly={isReadOnly} isDirty={isDirty} />
      </div>
      {data.enqueuedForDisbursementAt && (
        <div>
          <span className={'font-semibold'}>Initiated At:</span>{' '}
          {getDateString(DateFormat.shortDateTime, data.enqueuedForDisbursementAt, true)}
        </div>
      )}
      {data.sendAt && (
        <div>
          <span className={'font-semibold'}>Disbursed At:</span>{' '}
          {getDateString(DateFormat.shortDateTime, data.sendAt, true)}
        </div>
      )}
      {data.wellsFargoTransactionNumber && (
        <div>
          <span className={'font-semibold'}>Wells Fargo Transaction Number:</span> {data.wellsFargoTransactionNumber}
        </div>
      )}
      {data.voidedAt && (
        <div>
          <span className={'font-semibold'}>Voided Date:</span>{' '}
          {getDateString(DateFormat.shortDateTime, data.voidedAt, true)}
        </div>
      )}
      {data.voidReason && (
        <div>
          <span className={'font-semibold'}>Voided Reason:</span> {data.voidReason}
          {data.voidReasonDescription && ` - ${data.voidReasonDescription}`}
        </div>
      )}
      <div className={'flex basis-full justify-end'}>
        {showSend && <SendWireButton wireDisbursement={data} disabled={!canSend} />}
        <VoidWireButton wireDisbursement={data} />
      </div>
    </>
  );
};
